import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox, ClipboardCheck, Building2, Home as HomeIcon, Mail,
  CheckCircle2, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";

const Card = ({ icon: Icon, label, value, accent = "text-[#1E3A8A]", testid }) => (
  <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-soft" data-testid={testid}>
    <span className={`w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center ${accent}`}>
      <Icon className="w-5 h-5" />
    </span>
    <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="mt-1 text-3xl font-display font-bold text-[#0F172A]">{value ?? "—"}</p>
  </div>
);

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({})),
      api.get("/admin/dev/health").then((r) => setHealth(r.data)).catch(() => setHealth({})),
      api.get("/admin/dev/metrics").then((r) => setMetrics(r.data)).catch(() => setMetrics({})),
    ]);
  }, []);

  const emailOnline = !!health?.resend_configured && health?.backend_status === "online";
  const lastSuccess = metrics?.email?.last_successful_email_at;
  const lastFailed = metrics?.email?.last_failed_email_at;
  const lastFailedError = metrics?.email?.last_failed_email_error;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A]">Dashboard</h2>
          <p className="text-sm text-[#475569] mt-1">Overview of recent estimate activity.</p>
        </div>
        {emailOnline && (
          <span
            data-testid="production-ready-badge"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold tracking-wide"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Production Ready
          </span>
        )}
      </div>

      {/* Request counts */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Inbox} label="New requests" value={stats?.new} accent="text-[#1E3A8A]" testid="stat-new" />
        <Card icon={ClipboardCheck} label="Scheduled" value={stats?.scheduled} accent="text-[#10B981]" testid="stat-scheduled" />
        <Card icon={Building2} label="Commercial" value={stats?.commercial} accent="text-[#7C3AED]" testid="stat-commercial" />
        <Card icon={HomeIcon} label="Residential" value={stats?.residential} accent="text-[#0EA5E9]" testid="stat-residential" />
      </div>

      {/* Email system status (production) */}
      <div
        data-testid="email-system-status"
        className="mt-8 bg-white rounded-2xl border border-slate-100 p-6 shadow-soft"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-emerald-600" />
            </span>
            <div>
              <h3 className="font-display font-semibold text-[#0F172A] flex items-center gap-2">
                Email system
                <span data-testid="email-system-status-pill" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" />
                  {emailOnline ? "Online" : "Offline"}
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Resend production domain</p>
            </div>
          </div>
        </div>

        <dl className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 sm:border-0 sm:pb-0">
            <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sender</dt>
            <dd data-testid="email-sender" className="text-right text-[#0F172A] break-all">
              {health?.sender_name ? `${health.sender_name} <${health?.sender_email}>` : (health?.sender_email || "—")}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 sm:border-0 sm:pb-0">
            <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notification address</dt>
            <dd data-testid="email-notification-address" className="text-right text-[#0F172A] break-all">{health?.notification_email || "—"}</dd>
          </div>
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 sm:border-0 sm:pb-0">
            <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last successful send</dt>
            <dd data-testid="email-last-success" className="text-right text-[#0F172A]">{fmt(lastSuccess)}</dd>
          </div>
          <div className="flex items-start justify-between gap-3">
            <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last failed send</dt>
            <dd data-testid="email-last-failed" className="text-right">
              {lastFailed ? (
                <>
                  <div className="text-[#0F172A]">{fmt(lastFailed)}</div>
                  {lastFailedError && (
                    <div className="text-[11px] text-amber-700 mt-0.5 max-w-[280px]">{lastFailedError}</div>
                  )}
                </>
              ) : (
                <span className="text-slate-400 italic">None</span>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {/* Quick link */}
      <div className="mt-8">
        <Link to="/admin/requests" className="block bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-card transition-shadow" data-testid="dashboard-cta-requests">
          <Mail className="w-6 h-6 text-[#1E3A8A]" />
          <h3 className="mt-3 font-display font-semibold text-[#0F172A]">Manage estimate requests</h3>
          <p className="mt-1 text-sm text-[#475569]">Review new submissions, update statuses, and add internal notes.</p>
        </Link>
      </div>
    </div>
  );
}
